import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/feature/SeoHead';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://sink04.com';

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: '대구드레스룸 키친스',
    url: SITE_URL,
    telephone: '053-766-0411',
    image: `${SITE_URL}/og-image.jpg`,
    description: '대구 북구 침산동 드레스룸, 붙박이장, 팬트리 전문 시공업체 키친스',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '침산남로48',
      addressLocality: '북구',
      addressRegion: '대구광역시',
      addressCountry: 'KR',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 35.8868,
      longitude: 128.5731,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00',
      },
    ],
  },
];

const services = [
  {
    icon: 'ri-layout-masonry-line',
    title: '드레스룸',
    desc: '공간에 맞는 맞춤형 드레스룸 설계 및 시공. 수납 효율을 극대화한 구조로 제작합니다.',
    img: 'https://public.readdy.ai/ai/img_res/8aeb385bc21d763893c2dd65b5a519d1.jpg',
  },
  {
    icon: 'ri-door-closed-line',
    title: '붙박이장',
    desc: '벽면을 활용한 빌트인 붙박이장. 슬라이딩 도어부터 여닫이까지 다양한 스타일로 제작합니다.',
    img: 'https://readdy.ai/api/search-image?query=built-in%20wardrobe%20with%20sliding%20mirror%20doors%2C%20modern%20bedroom%20closet%2C%20sleek%20minimal%20design%2C%20white%20and%20wood%20tones%2C%20clean%20simple%20background&width=600&height=400&seq=home02&orientation=landscape',
  },
  {
    icon: 'ri-store-2-line',
    title: '팬트리',
    desc: '주방 수납 공간을 효율적으로 활용하는 팬트리 시공. 깔끔하고 실용적인 수납 솔루션.',
    img: 'https://readdy.ai/api/search-image?query=modern%20kitchen%20pantry%20storage%20cabinet%2C%20white%20built-in%20shelves%20with%20organized%20food%20items%20and%20kitchen%20supplies%2C%20clean%20minimal%20interior%2C%20bright%20simple%20background&width=600&height=400&seq=home03&orientation=landscape',
  },
];

const features = [
  { icon: 'ri-tools-line', title: '20년 이상 경력', desc: '풍부한 시공 경험으로 완성도 높은 결과물을 보장합니다.' },
  { icon: 'ri-ruler-2-line', title: '현장 맞춤 설계', desc: '공간 실측 후 최적화된 맞춤 설계로 낭비 없는 수납을 실현합니다.' },
  { icon: 'ri-shield-check-line', title: '품질 보증', desc: '시공 후 하자 발생 시 신속한 A/S로 고객 만족을 최우선합니다.' },
  { icon: 'ri-customer-service-2-line', title: '친절한 상담', desc: '방문 상담부터 시공 완료까지 전 과정을 꼼꼼하게 안내해드립니다.' },
];

const previewCases = [
  {
    seq: 'prev01',
    title: '화이트 드레스룸',
    tag: '드레스룸',
    img: 'https://readdy.ai/api/search-image?query=white%20wall-mounted%20open%20closet%20system%20dressing%20room%2C%20bright%20clean%20interior%2C%20organized%20hanging%20clothes%2C%20modern%20minimalist%20wardrobe%2C%20white%20shelves%20and%20drawers%2C%20simple%20white%20background&width=500&height=400&seq=prev01&orientation=landscape',
  },
  {
    seq: 'prev02',
    title: '블랙 드레스룸',
    tag: '드레스룸',
    img: 'https://readdy.ai/api/search-image?query=black%20matte%20wall-mounted%20open%20closet%20system%2C%20dark%20elegant%20dressing%20room%20interior%2C%20organized%20wardrobe%20with%20clothes%2C%20modern%20luxury%20design%2C%20dark%20metal%20shelves%2C%20simple%20dark%20background&width=500&height=400&seq=prev02&orientation=landscape',
  },
  {
    seq: 'prev03',
    title: '무드 라이팅 드레스룸',
    tag: '드레스룸',
    img: 'https://readdy.ai/api/search-image?query=modern%20walk-in%20closet%20with%20warm%20LED%20mood%20lighting%2C%20wooden%20shelves%2C%20hanging%20clothes%20rails%2C%20minimalist%20interior%2C%20beige%20tones%2C%20simple%20background&width=500&height=400&seq=prev03&orientation=landscape',
  },
  {
    seq: 'prev04',
    title: '팬트리 수납장',
    tag: '팬트리',
    img: 'https://readdy.ai/api/search-image?query=modern%20kitchen%20pantry%20storage%20cabinet%2C%20white%20built-in%20shelves%20with%20organized%20food%20items%2C%20clean%20minimal%20interior%2C%20bright%20kitchen%20storage%2C%20simple%20background&width=500&height=400&seq=prev04&orientation=landscape',
  },
];

function useCountUp(target: number, duration: number, start: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

function StatItem({ value, suffix, label, start }: { value: number; suffix: string; label: string; start: boolean }) {
  const count = useCountUp(value, 1500, start);
  return (
    <div className="text-center">
      <p className="text-4xl font-bold text-yellow-500">
        {count}<span className="text-2xl">{suffix}</span>
      </p>
      <p className="text-sm text-gray-500 mt-1">{label}</p>
    </div>
  );
}

export default function HomePage() {
  const statsRef = useRef<HTMLDivElement>(null);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SeoHead
        title="대구드레스룸 키친스 | 드레스룸·붙박이장·팬트리 전문 시공"
        description="대구 북구 침산동 드레스룸 전문업체 키친스. 드레스룸, 붙박이장, 팬트리 맞춤 시공. 20년 경력의 전문 시공팀이 최상의 결과물을 제공합니다. 053-766-0411"
        keywords="대구드레스룸, 드레스룸시공, 붙박이장, 팬트리, 대구북구수납장"
        canonical={SITE_URL}
        jsonLd={jsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative w-full h-[520px] md:h-[680px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=elegant%20modern%20walk-in%20closet%20dressing%20room%20interior%2C%20warm%20ambient%20lighting%2C%20organized%20wardrobe%20with%20hanging%20clothes%20and%20wooden%20shelves%2C%20luxury%20residential%20interior%20design%2C%20soft%20beige%20and%20warm%20white%20tones%2C%20professional%20interior%20photography%2C%20wide%20angle%20view%2C%20clean%20simple%20background&width=1400&height=700&seq=hero01&orientation=landscape"
          alt="대구드레스룸 키친스 시공 현장"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-black/10" />
        <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-16 lg:px-24">
          <div className="max-w-xl">
            <span className="inline-block text-xs font-semibold text-yellow-400 tracking-widest uppercase mb-4 border border-yellow-400/50 rounded-full px-3 py-1">
              대구 드레스룸 전문
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-4">
              공간을 바꾸면<br />
              <span className="text-yellow-400">생활이 달라집니다</span>
            </h1>
            <p className="text-sm md:text-base text-gray-200 leading-relaxed mb-8">
              드레스룸·붙박이장·팬트리 전문 시공업체 키친스.<br />
              20년 경력의 전문 시공팀이 최상의 결과물을 약속합니다.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold text-sm px-6 py-3 rounded-md transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line" />
                무료 상담 신청
              </Link>
              <Link
                to="/sample"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/30 font-medium text-sm px-6 py-3 rounded-md transition-colors whitespace-nowrap cursor-pointer"
              >
                시공사례 보기
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section ref={statsRef} className="bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          <StatItem value={20} suffix="년+" label="시공 경력" start={statsVisible} />
          <StatItem value={1500} suffix="+" label="누적 시공 건수" start={statsVisible} />
          <StatItem value={98} suffix="%" label="고객 만족도" start={statsVisible} />
          <StatItem value={24} suffix="h" label="A/S 응답 시간" start={statsVisible} />
        </div>
      </section>

      {/* Services */}
      <section className="py-20 max-w-6xl mx-auto px-6 w-full">
        <div className="text-center mb-12">
          <span className="text-xs font-semibold text-yellow-600 tracking-widest uppercase">Services</span>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-2">전문 시공 서비스</h2>
          <p className="text-sm text-gray-500 mt-2">공간의 특성에 맞는 최적의 수납 솔루션을 제공합니다</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((s) => (
            <div key={s.title} className="group rounded-lg overflow-hidden border border-gray-100 hover:border-yellow-200 transition-all cursor-pointer">
              <div className="w-full h-48 overflow-hidden">
                <img
                  src={s.img}
                  alt={s.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5">
                <div className="w-9 h-9 flex items-center justify-center rounded-md bg-yellow-50 mb-3">
                  <i className={`${s.icon} text-yellow-500 text-lg`} />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">{s.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Why Us */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold text-yellow-600 tracking-widest uppercase">Why Kitchens</span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-2">키친스를 선택하는 이유</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f) => (
              <div key={f.title} className="bg-white rounded-lg p-6 border border-gray-100">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-yellow-50 mb-4">
                  <i className={`${f.icon} text-yellow-500 text-xl`} />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Cases */}
      <section className="py-20 max-w-6xl mx-auto px-6 w-full">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="text-xs font-semibold text-yellow-600 tracking-widest uppercase">Portfolio</span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-2">최근 시공사례</h2>
          </div>
          <Link
            to="/sample"
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-yellow-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            전체보기 <i className="ri-arrow-right-line" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {previewCases.map((c) => (
            <Link key={c.seq} to="/sample" className="group block rounded-lg overflow-hidden border border-gray-100 cursor-pointer">
              <div className="w-full h-48 overflow-hidden">
                <img
                  src={c.img}
                  alt={c.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-4">
                <span className="text-xs text-yellow-600 font-medium bg-yellow-50 rounded-full px-2 py-0.5">{c.tag}</span>
                <p className="text-sm font-medium text-gray-800 mt-2">{c.title}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=luxury%20modern%20dressing%20room%20interior%2C%20warm%20ambient%20lighting%2C%20elegant%20wardrobe%20system%2C%20professional%20interior%20photography%2C%20soft%20warm%20tones%2C%20wide%20angle%2C%20clean%20background&width=1400&height=500&seq=cta01&orientation=landscape"
          alt="상담 신청 배경"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            지금 바로 무료 상담을 받아보세요
          </h2>
          <p className="text-sm text-gray-300 mb-8 leading-relaxed">
            현장 방문 실측부터 설계, 시공까지 전 과정을 책임집니다.<br />
            전화 또는 온라인으로 편하게 문의해주세요.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="tel:053-766-0411"
              className="inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold text-sm px-8 py-3 rounded-md transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line" />
              053-766-0411
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/30 font-medium text-sm px-8 py-3 rounded-md transition-colors whitespace-nowrap cursor-pointer"
            >
              온라인 상담 신청
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
